import {
  IconX,
  IconUser,
  IconMail,
  IconPhone,
  IconMapPin,
  IconCalendarClock,
  IconTruck,
  IconUserCheck,
  IconClipboardText,
  IconInfoCircle,
  IconPackage,
  IconHistory,
  IconPhoto,
  IconCloudDown,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFetchPickListShipmentById } from "../../../apis/pickupApis";
import ListView from "../../../components/common/ListView";
import NoDataFound from "../../../components/common/NoDataFound";
import toast from "react-hot-toast";
import formatDate, { formatDateWithTime } from "../../../utilities/helper";
import { useLanguage } from "../../../context/LanguageContext";

type Props = {
  open: boolean;
  onClose: () => void;
  pickup: any;
  isSidebar?: boolean;
  onOpenAssignDriver?: () => void;
  onOpenPickupCollect?: (selectedIds: string[]) => void;
  onOpenRiwVerification?: (selectedIds: string[]) => void;
  onOpenAssignReceiver?: () => void;
  selectedIds?: string[];
  setSelectedIds?: React.Dispatch<React.SetStateAction<string[]>>;
  onSelectionActionsChange?: (
    actions: {
      type: "assign" | "collect" | "inbound" | "assign-staff";
      ids: string[];
    } | null,
  ) => void;
};

const Row = ({
  icon,
  label,
  value,
  className,
}: {
  icon?: React.ReactNode;
  label?: string;
  className?: string;
  value: React.ReactNode;
}) => (
  <div className={`flex items-center justify-between gap-2 py-1 min-w-0 ${className || ""}`}>
    <div className="flex items-center gap-1.5 min-w-0 flex-shrink-0">
      {icon && <div className="text-gray-400 flex-shrink-0">{icon}</div>}
      {label && (
        <span className="font-medium text-xs text-[#212121] whitespace-nowrap">
          {label}
        </span>
      )}
    </div>
    <div className="text-right min-w-0 flex-1">
      <span className="text-xs text-[#212121] font-medium truncate block" title={typeof value === "string" ? value : undefined}>
        {value || ""}
      </span>
    </div>
  </div>
);

const SectionHeader = ({
  icon: Icon,
  title,
  className = "border-b border-[#FBD037]/30 pb-2 mb-3",
}: {
  icon: any;
  title: string;
  className?: string;
}) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
      <Icon size={16} className="text-[#212121]" stroke={1.5} />
    </div>
    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
      {title}
    </p>
  </div>
);

type PickupRequest = {
  id?: string;
  awbNumber?: string;
  awb?: string;
  status: string;
  [key: string]: any;
};

const AsyncT: React.FC<{ text: string }> = ({ text }) => {
  const { translateText, language } = useLanguage();
  const [translated, setTranslated] = useState(text);

  useEffect(() => {
    let active = true;
    if (language === "en") {
      setTranslated(text);
      return;
    }
    translateText(text).then((res) => {
      if (active) setTranslated(res);
    });
    return () => {
      active = false;
    };
  }, [text, translateText, language]);

  return <>{translated}</>;
};

const PickupViewModal = ({
  open,
  onClose,
  pickup,
  isSidebar,
  onOpenAssignDriver,
  onOpenPickupCollect,
  onOpenRiwVerification,
  onOpenAssignReceiver,
  selectedIds: propsSelectedIds,
  setSelectedIds: propsSetSelectedIds,
  onSelectionActionsChange,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<
    "OVERVIEW" | "CARGO ITEMS" | "ACTIVITY LOG" | "POD MEDIA"
  >("OVERVIEW");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>([]);
  const selectedIds =
    propsSelectedIds !== undefined ? propsSelectedIds : localSelectedIds;
  const setSelectedIds =
    propsSetSelectedIds !== undefined
      ? propsSetSelectedIds
      : setLocalSelectedIds;
  const [expandedAssignments, setExpandedAssignments] = useState<
    Record<string, boolean>
  >({});

  const pickupId = pickup?.id || pickup?.manifestId || "";
  const {
    data: pickupData,
    isFetching,
    refetch,
  } = useFetchPickListShipmentById(1, 100, "", pickupId);

  React.useEffect(() => {
    if (open && pickupId) {
      refetch();
    }
  }, [open, pickupId, refetch]);

  const shipmentsList = React.useMemo(() => {
    if (!pickupData) return [];

    const nonReverted =
      pickupData.nonRevertedShipments ||
      pickupData.generatedShipments ||
      pickupData.totalShipments?.rows ||
      [];
    const reverted = (pickupData.revertStatuses || []).map((item: any) => ({
      ...item,
      id: item.shipmentId || item.id,
      isReverted: true,
    }));

    const nonRevertedAwbs = new Set(
      nonReverted.map((s: any) => s.awbNumber || s.awb),
    );
    const uniqueReverted = reverted.filter(
      (s: any) => !nonRevertedAwbs.has(s.awbNumber || s.awb),
    );

    const combined = [...nonReverted, ...uniqueReverted];
    return combined.filter((s: any) => {
      const status = (s.status || s.to || "")
        .toUpperCase()
        .replace(/_/g, " ")
        .trim();
      return status !== "NEW ORDER" && status !== "NEW_ORDER";
    });
  }, [pickupData]);

  const filteredShipments = React.useMemo(() => {
    if (statusFilter === "ALL") return shipmentsList;
    return shipmentsList.filter((s: any) => {
      const status = (s.status || s.to || "").toUpperCase();
      if (statusFilter === "REVERTED") {
        return status === "NEW_ORDER" || status === "READY_FOR_PICKUP";
      }
      return status === statusFilter;
    });
  }, [shipmentsList, statusFilter]);

  React.useEffect(() => {
    if (!onSelectionActionsChange) return;

    if (selectedIds.length === 0) {
      onSelectionActionsChange(null);
      return;
    }

    const hasPickupGenerated = filteredShipments.some(
      (s: any) =>
        selectedIds.includes(s.id) &&
        (s.status || s.to || "").toUpperCase() === "PICKUP_GENERATED",
    );
    const hasReadyForPickup = filteredShipments.some(
      (s: any) =>
        selectedIds.includes(s.id) &&
        (s.status || s.to || "").toUpperCase() === "READY_FOR_PICKUP",
    );
    const hasCollected = filteredShipments.some(
      (s: any) =>
        selectedIds.includes(s.id) &&
        ["PICKUP_COLLECTED", "COLLECTED"].includes(
          (s.status || s.to || "").toUpperCase(),
        ),
    );
    const hasAssignedToStaff = filteredShipments.some(
      (s: any) =>
        selectedIds.includes(s.id) &&
        (s.status || s.to || "").toUpperCase() === "ASSIGNED_TO_STAFF",
    );

    if (hasPickupGenerated) {
      onSelectionActionsChange({ type: "assign", ids: selectedIds });
    } else if (hasReadyForPickup) {
      const readyIds = filteredShipments
        .filter(
          (s: any) =>
            selectedIds.includes(s.id) &&
            (s.status || s.to || "").toUpperCase() === "READY_FOR_PICKUP",
        )
        .map((s: any) => s.id);
      onSelectionActionsChange({ type: "collect", ids: readyIds });
    } else if (hasCollected) {
      const collectedIds = filteredShipments
        .filter(
          (s: any) =>
            selectedIds.includes(s.id) &&
            ["PICKUP_COLLECTED", "COLLECTED"].includes(
              (s.status || s.to || "").toUpperCase(),
            ),
        )
        .map((s: any) => s.id);
      onSelectionActionsChange({ type: "assign-staff", ids: collectedIds });
    } else if (hasAssignedToStaff) {
      const staffIds = filteredShipments
        .filter(
          (s: any) =>
            selectedIds.includes(s.id) &&
            (s.status || s.to || "").toUpperCase() === "ASSIGNED_TO_STAFF",
        )
        .map((s: any) => s.id);
      onSelectionActionsChange({ type: "inbound", ids: staffIds });
    } else {
      onSelectionActionsChange(null);
    }
  }, [selectedIds, filteredShipments, onSelectionActionsChange]);

  if (!open || !pickup) return null;

  const details = {
    ...pickup,
    ...pickupData,
  };
  const statusHistories = pickupData?.statusHistories || [];

  const handleDownload = (docUrl?: string) => {
    if (!docUrl) return;
    window.open(docUrl, "_blank");
  };

  const content = (
    <div className="space-y-2">
      {/* Tabs */}
      <div className="flex gap-2  border-b border-gray-200/50 pb-2">
        {(
          [
            {
              key: "OVERVIEW",
              icon: <IconInfoCircle size={18} />,
              label: t("Overview"),
            },
            {
              key: "CARGO ITEMS",
              icon: <IconPackage size={18} />,
              label: t("AWB"),
            },
            {
              key: "ACTIVITY LOG",
              icon: <IconHistory size={18} />,
              label: t("Timeline"),
            },
            {
              key: "POD MEDIA",
              icon: <IconPhoto size={18} />,
              label: t("POD Media"),
            },
          ] as const
        ).map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1.5 border ${
              activeTab === tab.key
                ? "bg-[#FBD037]/20 text-[#000000] border-gray-200 shadow-sm"
                : "bg-slate-100 hover:bg-slate-200/60 text-slate-600 border-gray-200"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {isFetching && !pickupData ? (
        <div className="flex justify-center py-12 text-sm text-gray-500 animate-pulse">
          Loading details...
        </div>
      ) : (
        <>
          {activeTab === "OVERVIEW" && (
            <div className="space-y-2">
              {/* Manifest Specifications Card */}
              {/* <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm hover:shadow-md">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                <SectionHeader icon={IconClipboardText} title="Manifest" />
                <div className="space-y-2 relative">
                  <Row label="Manifest ID" value={details.manifestId || details.pickupId} />
                  <Row
                    label="Status"
                    value={
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-semibold border ${
                        details.status === "COMPLETED"
                          ? "bg-green-50 text-green-700 border-green-200"
                          : "bg-blue-50 text-blue-700 border-blue-200"
                      }`}>
                        {(details.status || "").replace(/_/g, " ")}
                      </span>
                    }
                  />
                  <Row label="Type" value="Shipment Pickup" />
                  <Row label="Date" value={formatDate(details.pickupDate || details.date || details.createdAt)} />
                  <Row label="Time Slot" value={details.timeSlot} />
                </div>
              </div> */}

              {/* Sender & Address Information */}
              <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm hover:shadow-md">
                <div className="flex justify-between items-center border-b border-[#FBD037]/30 pb-2 mb-3">
                  <SectionHeader className="" icon={IconUser} title={t("Client")} />
                  <Row
                    // icon={<IconUser size={14} />}
                    // label="Client Name"
                    value={details.clientName || details.client}
                  />
                </div>
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-4">
                  <Row
                    icon={<IconPhone size={14} />}
                    label={t("Phone")}
                    value={details.mobileNo || details.clientMobile}
                  />
                  <Row
                    icon={<IconMail size={14} />}
                    label={t("Email")}
                    value={details.email}
                  />
                  <Row
                    icon={<IconMapPin size={14} />}
                    label={t("Address")}
                    value={
                      <p
                        title={
                          details.fullAddress ||
                          details.locationName ||
                          details.location
                        }
                        className="truncate w-[150px]"
                      >
                        {details.fullAddress ||
                          details.locationName ||
                          details.location}
                      </p>
                    }
                  />
                </div>
              </div>

              {/* Driver Details */}
              {details.driverAssignments &&
              details.driverAssignments.length > 0 ? (
                <div className="space-y-4">
                  {(() => {
                    const groupedAssignments: any[] = [];
                    (details.driverAssignments || []).forEach(
                      (assignment: any) => {
                        const existing = groupedAssignments.find(
                          (g) =>
                            (assignment.driverId &&
                              g.driverId === assignment.driverId) ||
                            (assignment.driverCode &&
                              g.driverCode === assignment.driverCode),
                        );
                        if (existing) {
                          existing.shipments = [
                            ...existing.shipments,
                            ...(assignment.shipments || []),
                          ];
                        } else {
                          groupedAssignments.push({
                            ...assignment,
                            shipments: [...(assignment.shipments || [])],
                          });
                        }
                      },
                    );

                    return groupedAssignments.map(
                      (assignment: any, assignIdx: number) => {
                        const assignedShipments = (
                          assignment.shipments || []
                        ).filter((ship: any) => {
                          return shipmentsList.some(
                            (s: any) =>
                              s.awbNumber === ship.awb ||
                              s.awb === ship.awb ||
                              s.id === ship.id,
                          );
                        });
                        const assignmentHistory = statusHistories.find(
                          (h: any) =>
                            h.newStatus === "ASSIGNED" || h.newStatus === "ASSIGNED_TO_DRIVER"
                        );
                        return (
                          <div
                            key={assignment.id || assignIdx}
                            className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm hover:shadow-md relative"
                          >
                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/10 to-transparent pointer-events-none" />
                            <div className="flex justify-between items-center border-b border-[#FBD037]/30 pb-2 mb-3">
                              <SectionHeader
                                className=""
                                icon={IconUserCheck}
                                title={t("Driver Assigned")}
                              />
                              <Row
                                icon={<IconUserCheck size={14} />}
                                label={t("Driver Name")}
                                value={assignment.driverName || "—"}
                              />
                            </div>
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-4">
                              <Row
                                icon={<IconClipboardText size={14} />}
                                label={t("Driver Code")}
                                value={assignment.driverCode || "—"}
                              />
                              <Row
                                icon={<IconPhone size={14} />}
                                label={t("Contact")}
                                value={assignment.driverMobileNo || "—"}
                              />
                              <Row
                                icon={<IconTruck size={14} />}
                                label={t("Vehicle")}
                                value={assignment.vehicleType || "—"}
                              />
                              {assignmentHistory && (
                                <>
                                  <Row
                                    icon={<IconUser size={14} />}
                                    label={t("Assigned By")}
                                    value={assignmentHistory.changedByName || "—"}
                                  />
                                  <Row
                                    icon={<IconCalendarClock size={14} />}
                                    label={t("Assigned At")}
                                    value={assignmentHistory.createdAt ? formatDateWithTime(assignmentHistory.createdAt) : "—"}
                                  />
                                </>
                              )}
                            </div>
                            {/* List of assigned AWBs for this driver */}
                            {assignedShipments.length > 0 &&
                              (() => {
                                const isExpanded =
                                  !!expandedAssignments[
                                    assignment.id || `idx-${assignIdx}`
                                  ];
                                return (
                                  <div className="mt-3 pt-3 border-t border-dashed border-slate-200 relative z-10">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const key =
                                          assignment.id || `idx-${assignIdx}`;
                                        setExpandedAssignments((prev) => ({
                                          ...prev,
                                          [key]: !prev[key],
                                        }));
                                      }}
                                      className="w-full flex items-center justify-between py-1 text-slate-600 hover:text-[#212121] transition-colors focus:outline-none"
                                    >
                                      <span className="text-[10px] font-black text-[#212121]/70 uppercase tracking-widest flex items-center gap-1.5">
                                        AWB ({assignedShipments.length})
                                      </span>
                                      <div className="p-1 rounded bg-slate-100 hover:bg-slate-200/80 transition-colors">
                                        {isExpanded ? (
                                          <IconChevronUp
                                            size={14}
                                            className="text-slate-500"
                                          />
                                        ) : (
                                          <IconChevronDown
                                            size={14}
                                            className="text-slate-500"
                                          />
                                        )}
                                      </div>
                                    </button>

                                    {isExpanded && (
                                      <div className="space-y-2 mt-2 transition-all duration-300">
                                        {assignedShipments.map(
                                          (ship: any, idx: number) => {
                                            // Find full info from shipmentsList
                                            const fullAwb = shipmentsList.find(
                                              (s: any) =>
                                                s.awbNumber === ship.awb ||
                                                s.awb === ship.awb ||
                                                s.id === ship.id,
                                            );
                                            const actualStatus = (
                                              fullAwb?.status ||
                                              ship.status ||
                                              ""
                                            ).toUpperCase();
                                            const formattedStatus = actualStatus
                                              .toLowerCase()
                                              .replace(/_/g, " ")
                                              .replace(
                                                /\w/g,
                                                (char: string) =>
                                                  char.toUpperCase(),
                                              );

                                            return (
                                              <div
                                                key={idx}
                                                className="bg-white/80 border border-slate-200/60 rounded-xl p-2.5 flex items-center justify-between shadow-2xs hover:border-slate-300 transition-colors"
                                              >
                                                <div className="flex flex-col gap-0.5">
                                                  <span
                                                    onClick={() =>
                                                      navigate(
                                                        `/orders/${ship.awb}`,
                                                      )
                                                    }
                                                    className="font-bold text-xs text-blue-600 hover:underline cursor-pointer"
                                                  >
                                                    {ship.awb || "—"}
                                                  </span>
                                                  {/* {fullAwb && (
                                                    <span className="text-[10px] text-slate-500 font-medium">
                                                      Dest: {fullAwb.receiverCity || "—"}
                                                    </span>
                                                  )} */}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  <span
                                                    className={`rounded-full px-2 py-0.5 border text-[9px] font-semibold ${
                                                      actualStatus ===
                                                        "PICKUP_GENERATED" ||
                                                      actualStatus ===
                                                        "READY_FOR_PICKUP"
                                                        ? "bg-blue-50 text-blue-700 border-blue-200"
                                                        : [
                                                              "PICKUP_COLLECTED",
                                                              "COLLECTED",
                                                            ].includes(
                                                              actualStatus,
                                                            )
                                                          ? "bg-amber-50 text-amber-700 border-amber-200"
                                                          : "bg-green-50 text-green-700 border-green-200"
                                                    }`}
                                                  >
                                                    {formattedStatus}
                                                  </span>

                                                  {/* Action buttons directly on click/hover */}
                                                  <div className="flex items-center gap-1">
                                                    {(actualStatus ===
                                                      "PICKUP_GENERATED" ||
                                                      actualStatus ===
                                                        "READY_FOR_PICKUP") &&
                                                      onOpenPickupCollect && (
                                                        <button
                                                          onClick={() =>
                                                            onOpenPickupCollect(
                                                              [
                                                                fullAwb?.id ||
                                                                  ship.id,
                                                              ],
                                                            )
                                                          }
                                                          className="px-2 py-0.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded text-[9px] font-bold shadow-2xs transition-all"
                                                        >
                                                          {t("Collect")}
                                                        </button>
                                                      )}
                                                    {[
                                                      "PICKUP_COLLECTED",
                                                      "COLLECTED",
                                                    ].includes(actualStatus) && (
                                                      <>
                                                        {onOpenAssignReceiver && (
                                                          <button
                                                            onClick={() => {
                                                              if (setSelectedIds) {
                                                                setSelectedIds([fullAwb?.id || ship.id]);
                                                              }
                                                              onOpenAssignReceiver();
                                                            }}
                                                            className="px-2 py-0.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded text-[9px] font-bold shadow-2xs transition-all"
                                                          >
                                                            {t("Assign Staff")}
                                                          </button>
                                                        )}
                                                        {onOpenRiwVerification && (
                                                          <button
                                                            onClick={() =>
                                                              onOpenRiwVerification(
                                                                [
                                                                  fullAwb?.id ||
                                                                    ship.id,
                                                                ],
                                                              )
                                                            }
                                                            className="px-2 py-0.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded text-[9px] font-bold shadow-2xs transition-all"
                                                          >
                                                            {t("Inbound")}
                                                          </button>
                                                        )}
                                                      </>
                                                    )}
                                                    {actualStatus ===
                                                      "ASSIGNED_TO_STAFF" &&
                                                      onOpenRiwVerification && (
                                                        <button
                                                          onClick={() =>
                                                            onOpenRiwVerification(
                                                              [
                                                                fullAwb?.id ||
                                                                  ship.id,
                                                              ],
                                                            )
                                                          }
                                                          className="px-2 py-0.5 bg-[#FBD037] hover:bg-[#FBD037]/80 text-[#212121] rounded text-[9px] font-bold shadow-2xs transition-all"
                                                        >
                                                          {t("Inbound")}
                                                        </button>
                                                      )}
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          },
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                          </div>
                        );
                      },
                    );
                  })()}
                </div>
              ) : (
                details.driverCode && (() => {
                  const assignmentHistory = statusHistories.find(
                    (h: any) => h.newStatus === "ASSIGNED" || h.newStatus === "ASSIGNED_TO_DRIVER"
                  );
                  return (
                    <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm hover:shadow-md">
                      <SectionHeader
                        icon={IconUserCheck}
                        title={t("Driver Details")}
                      />
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                      <div className="grid grid-cols-2 lg:grid-cols-3 relative gap-y-3 gap-x-4">
                        <Row
                          icon={<IconUserCheck size={14} />}
                          label={t("Driver Name")}
                          value={details.driverName || details.assignedResource}
                        />
                        <Row
                          icon={<IconClipboardText size={14} />}
                          label={t("Driver Code")}
                          value={details.driverCode}
                        />
                        <Row
                          icon={<IconPhone size={14} />}
                          label={t("Contact")}
                          value={details.driverMobileNo}
                        />
                        <Row
                          icon={<IconTruck size={14} />}
                          label={t("Vehicle")}
                          value={details.vehicleType}
                        />
                        {assignmentHistory && (
                          <>
                            <Row
                              icon={<IconUser size={14} />}
                              label={t("Assigned By")}
                              value={assignmentHistory.changedByName || "—"}
                            />
                            <Row
                              icon={<IconCalendarClock size={14} />}
                              label={t("Assigned At")}
                              value={assignmentHistory.createdAt ? formatDateWithTime(assignmentHistory.createdAt) : "—"}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()
              )}
            </div>
          )}

          {activeTab === "CARGO ITEMS" && (
            <div className="space-y-4 font-sans text-xs">
              {/* ASSOCIATED WAYBILLS SECTION */}
              <div className="space-y-3">
                <div className="flex items-center justify-between
                ">
                  <div className="flex items-center gap-2">
                     
                  </div>
                  <div className="flex items-center gap-1.5">
                    {(() => {
                      const selectableShipments = filteredShipments.filter((s: any) =>
                        ["PICKUP_GENERATED", "READY_FOR_PICKUP", "PICKUP_COLLECTED", "COLLECTED", "ASSIGNED_TO_STAFF"].includes((s.status || s.to || "").toUpperCase())
                      );
                      const isChecked = selectableShipments.length > 0 && selectableShipments.every((s: any) => selectedIds.includes(s.id));
                      return (
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            if (selectableShipments.length === 0) return;
                            
                            const statuses = selectableShipments.map((s: any) => {
                              const status = (s.status || s.to || "").toUpperCase();
                              if (["PICKUP_COLLECTED", "COLLECTED"].includes(status)) {
                                return "COLLECTED";
                              }
                              return status;
                            });
                            const allSameStatus = new Set(statuses).size <= 1;
                            
                            if (!allSameStatus) {
                              toast.error("Please filter by status first to select all shipments.");
                              return;
                            }
                            
                            const allSelected = selectableShipments.every((s: any) => selectedIds.includes(s.id));
                            if (setSelectedIds) {
                              if (allSelected) {
                                setSelectedIds([]);
                              } else {
                                setSelectedIds(selectableShipments.map((s: any) => s.id));
                              }
                            }
                          }}
                          className="w-3.5 h-3.5 rounded border-slate-300 text-[#FBD037] focus:ring-[#FBD037] cursor-pointer mr-1"
                          title="Select All"
                        />
                      );
                    })()}
                    <span className="font-semibold text-slate-500 text-[10px] uppercase tracking-wider">
                      {t("Filter")}:
                    </span>
                    <select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setSelectedIds([]); // Clear selection when filter changes
                      }}
                      className="bg-white border border-slate-200 text-[#212121] rounded-lg px-2 py-0.5 text-xs font-semibold focus:outline-none focus:border-[#FBD037]/80 cursor-pointer shadow-sm"
                    >
                      <option value="ALL">{t("All Statuses")}</option>
                      <option value="PICKUP_GENERATED">{t("Pickup Generated")}</option>
                      <option value="READY_FOR_PICKUP">{t("Ready for Pickup")}</option>
                      <option value="PICKUP_COLLECTED">{t("Pickup Collected")}</option>
                      <option value="ASSIGNED_TO_STAFF">
                        {t("Assigned To Staff")}
                      </option>
                      <option value="RECEIVE_IN_WAREHOUSE">
                        {t("Receive in Warehouse")}
                      </option>
                      <option value="REVERTED">{t("Reverted / New Order")}</option>
                    </select>
                  </div>
                </div>

                {/* Actions for Selected Items */}
                {selectedIds.length > 0 && (
                  <div className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200/60 rounded-2xl mb-3 shadow-2xs">
                    <div className="text-[10px] font-medium text-slate-500 flex items-center gap-2">
                      <span>
                        {selectedIds.length} {t("waybills selected")}
                      </span>
                      <button
                        onClick={() => setSelectedIds([])}
                        className="text-[#212121] hover:underline font-bold"
                      >
                        {t("Clear")}
                      </button>
                    </div>
                  </div>
                )}

                {filteredShipments.length > 0 ? (
                  <div className="space-y-3">
                    {filteredShipments.map((awb: any, index: number) => {
                      const formattedStatus = (awb.status || "")
                        .toLowerCase()
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (char: string) => char.toUpperCase());

                      const getStatusClasses = (status: string) => {
                        const lower = (status || "").toLowerCase();
                        if (lower === "reverted") {
                          return "bg-rose-50 text-rose-700 border-rose-200";
                        }
                        if (
                          lower.includes("delivery") ||
                          lower.includes("dispatch") ||
                          lower.includes("loaded")
                        ) {
                          return "bg-green-50 text-green-700 border-green-200";
                        } else if (
                          lower.includes("delivered") ||
                          lower.includes("completed")
                        ) {
                          return "bg-gray-50 text-gray-700 border-gray-200";
                        } else if (lower.includes("undelivered")) {
                          return "bg-amber-50 text-amber-700 border-amber-200";
                        }
                        return "bg-blue-50 text-blue-700 border-blue-200";
                      };

                      const dest =
                        [
                          awb.receiverCity,
                          awb.receiverDistrict,
                          awb.receiverCountry,
                        ]
                          .filter(Boolean)
                          .join(", ") || "—";

                      const items =
                        awb.olineItems ||
                        awb.oLineItems ||
                        awb.items ||
                        awb.products ||
                        [];
                      const isSelected = selectedIds.includes(awb.id);

                      return (
                        <div
                          key={awb.id || index}
                          className="group relative rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 shadow-sm hover:shadow-md space-y-2 text-xs"
                        >
                          <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                          <div className="relative space-y-2">
                            {awb.isReverted && (
                              <div className="text-[10px] text-rose-600 font-bold bg-rose-50 border border-rose-100 rounded-lg p-1.5 mb-2 flex items-center gap-1.5">
                                <IconInfoCircle
                                  size={12}
                                  className="flex-shrink-0"
                                />
                                <span>
                                  Reverted to{" "}
                                  {awb.to
                                    ?.replace(/_/g, " ")
                                    .toLowerCase()
                                    .replace(/\b\w/g, (c: string) =>
                                      c.toUpperCase(),
                                    )}{" "}
                                  {awb.reason ? `(Reason: ${awb.reason})` : ""}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-2">
                                {[
                                  "PICKUP_GENERATED",
                                  "READY_FOR_PICKUP",
                                  "PICKUP_COLLECTED",
                                  "COLLECTED",
                                  "ASSIGNED_TO_STAFF",
                                ].includes(
                                  (awb.status || awb.to || "").toUpperCase(),
                                ) && (
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        if (selectedIds && selectedIds.length > 0) {
                                          const firstSelectedId = selectedIds[0];
                                          const firstSelectedShipment = filteredShipments.find(s => s.id === firstSelectedId);
                                          const firstStatus = (firstSelectedShipment?.status || firstSelectedShipment?.to || "").toUpperCase();
                                          const currentStatus = (awb.status || awb.to || "").toUpperCase();

                                          const isFirstCollected = ["PICKUP_COLLECTED", "COLLECTED"].includes(firstStatus);
                                          const isCurrentCollected = ["PICKUP_COLLECTED", "COLLECTED"].includes(currentStatus);

                                          const isSameStatus = (isFirstCollected && isCurrentCollected) || (firstStatus === currentStatus);

                                          if (!isSameStatus) {
                                            toast.error("You can only select shipments with the same status.");
                                            return;
                                          }
                                        }
                                        if (setSelectedIds) {
                                          setSelectedIds((prev) => [...prev, awb.id]);
                                        }
                                      } else {
                                        if (setSelectedIds) {
                                          setSelectedIds((prev) => prev.filter((id) => id !== awb.id));
                                        }
                                      }
                                    }}
                                    className="w-3.5 h-3.5 rounded border-slate-300 text-[#FBD037] focus:ring-[#FBD037] cursor-pointer"
                                  />
                                )}
                                <span
                                  onClick={() =>
                                    navigate(
                                      `/orders/${awb.awbNumber || awb.awb}`,
                                    )
                                  }
                                  className="font-bold text-sm text-blue-600 hover:underline cursor-pointer"
                                >
                                  {awb.awbNumber || awb.awb || "—"}
                                </span>
                              </div>
                              <span
                                className={`rounded-full px-2.5 py-0.5 border text-[10px] font-semibold ${getStatusClasses(awb.status)}`}
                              >
                                {t(formattedStatus) || t("Loaded")}
                              </span>
                            </div>
                            {/* <div className="text-slate-500 font-medium">
                              Customer: <span className="text-slate-700">{awb.customerName || awb.receiverName || "—"}</span> • Dest: <span className="text-slate-700">{dest}</span>
                            </div>
                            <div className="flex justify-between items-center text-[11px] text-slate-400">
                              <div>
                                Weight: <span className="text-slate-600 font-medium">{awb.weight ? `${awb.weight} kg` : "—"}</span>
                                {" • "}
                                Pcs: <span className="text-slate-600 font-medium">{awb.pieces || awb.pcs || "1"}</span>
                              </div>
                              <div className="font-semibold text-slate-600 text-xs">
                                {(() => {
                                  const pType = (awb.paymentType || awb.payment_type || "").toUpperCase();
                                  if (pType === "COD" || (!pType && awb.codAmount && Number(awb.codAmount) > 0)) {
                                    return `${t("COD")}: ${awb.codAmount != null ? awb.codAmount : "N/A"}`;
                                  }
                                  if (pType === "CC") {
                                    return t("CC Paid");
                                  }
                                  return pType || t("CC Paid");
                                })()}
                              </div>
                            </div> */}


                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="w-full p- border border-[#D2D2D2]/70 rounded-2xl bg-gradient-to-b from-white/90 to-white/60 shadow-sm text-center text-xs text-slate-400">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/10 to-transparent pointer-events-none" />
                    <div className="relative">
                      {t("No Associated Waybills")}
                      <div className="mt-1 text-[11px] text-slate-400 font-medium">
                        {t("No raw physical shipments registered to this manifest file")}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "ACTIVITY LOG" && (
            <div className="relative rounded-2xl bg-gradient-to-b from-white/90 to-white/60 border border-[#D2D2D2]/70 p-4 shadow-sm">
              {/* <SectionHeader
                icon={IconHistory}
                title="Timeline"
              /> */}
              {statusHistories.length > 0 ? (
                <div className="space-y-0 relative pl-4 border-l border-emerald-200/60 ml-2">
                  {statusHistories.map((item: any, index: number) => {
                    const isLast = index === statusHistories.length - 1;
                    return (
                      <div
                        key={item.id || index}
                        className="relative pb-6 last:pb-0"
                      >
                        {/* Dot Indicator */}
                        {isLast ? (
                          <div className="absolute -left-[24px] top-1 w-4 h-4 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-md border border-white ring-2 ring-emerald-600/30">
                            <IconCheck size={10} stroke={4} />
                          </div>
                        ) : (
                          <div className="absolute -left-[22px] top-1.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white ring-2 ring-emerald-500/20 shadow-xs" />
                        )}
                        <div>
                          <div className="flex justify-between items-center">
                          <p className="text-xs font-semibold text-slate-800">
                            {t((item.newStatus || "")
                              .replace(/_/g, " ")
                              .toLowerCase()
                              .replace(/\b\w/g, (c: string) => c.toUpperCase()))}
                          </p>

                          <p className="text-[10px] text-slate-400 mt-1">
                            {formatDateWithTime(item.createdAt)}
                          </p>
                          </div>
                          {item.details && (
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              <AsyncT text={item.details} />
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-8 text-center text-xs text-slate-400">
                  No activity history logs found.
                </div>
              )}
            </div>
          )}

          {activeTab === "POD MEDIA" && (
            <div className="space-y-4">
              {/* Customer Digital Signature Card */}
              <div className="rounded-2xl border border-[#D2D2D2]/70 bg-gradient-to-b from-white/90 to-white/60 p-4 shadow-sm">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-center border-b pb-2">
                  {t("Customer Digital Signature")}
                </h4>
                <div className="flex flex-col items-center justify-center py-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  {details.signature ? (
                    <div className="flex flex-col items-center gap-3">
                      <img
                        src={details.signature}
                        alt="Customer Signature"
                        className="max-h-24 object-contain rounded-lg border border-slate-200 bg-white p-1"
                      />
                      <button
                        onClick={() => handleDownload(details.signature)}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold"
                      >
                        <IconCloudDown size={16} /> {t("Download Signature")}
                      </button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="text-xs text-slate-500 italic">
                        {t("No signature file uploaded")}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-1">
                        {t("status: sign-off pending")}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Cargo Photo Verification */}
              <div className="rounded-2xl border border-[#D2D2D2]/70 bg-gradient-to-b from-white/90 to-white/60 p-4 shadow-sm">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-center border-b pb-2">
                  {t("Cargo Photo Verification")}
                </h4>
                <div className="flex flex-col items-center justify-center py-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  {details.image || details.pickupImage ? (
                    <div className="flex flex-col items-center gap-3 w-full px-4">
                      <img
                        src={details.image || details.pickupImage}
                        alt="Cargo Verification"
                        className="max-h-40 object-contain rounded-lg border border-slate-200 bg-white p-1"
                      />
                      <button
                        onClick={() =>
                          handleDownload(details.image || details.pickupImage)
                        }
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold"
                      >
                        <IconCloudDown size={16} /> {t("Download Cargo Photo")}
                      </button>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-slate-400 flex flex-col items-center gap-2">
                      <IconPhoto
                        size={32}
                        className="opacity-30 text-[#212121]"
                      />
                      <p className="text-xs italic">
                        {t("No cargo photo verification media available")}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  if (isSidebar) {
    return <div className="p-6">{content}</div>;
  }

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="rounded-3xl w-full md:w-10/12 lg:w-9/12 bg-white flex flex-col max-h-[90vh] overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <h1 className="text-xl font-bold text-gray-800">
            View Request: {details.manifestId || details.pickupId}
          </h1>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <IconX size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{content}</div>
      </div>
    </div>
  );
};

export default PickupViewModal;
